/**
 * Hook for fetching and managing Dotypos busyness analytics data
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  BusynessHourlyMetric,
  BusynessStats,
  DotyposSyncLog,
  HourlyData,
  DayOfWeekData,
  HeatmapCell,
  PeakTime,
  PeakDay,
  DAY_NAMES,
  SyncOrdersResponse,
} from '@/types/dotypos-busyness';
import { toast } from '@/hooks/ui/use-toast';

interface UseDotyposBusynessOptions {
  businessId: string;
  dateFrom?: string; // YYYY-MM-DD format
  dateTo?: string; // YYYY-MM-DD format
  enabled?: boolean;
}

export function useDotyposBusyness({
  businessId,
  dateFrom,
  dateTo,
  enabled = true,
}: UseDotyposBusynessOptions) {
  const queryClient = useQueryClient();

  // Calculate default date range if not provided (last 30 days)
  const effectiveDateFrom = dateFrom || (() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  })();

  const effectiveDateTo = dateTo || new Date().toISOString().split('T')[0];

  // Calculate number of days for stats calculation
  const daysDiff = Math.ceil(
    (new Date(effectiveDateTo).getTime() - new Date(effectiveDateFrom).getTime()) / (1000 * 60 * 60 * 24)
  ) + 1;

  // Fetch hourly metrics from database
  const {
    data: hourlyMetrics,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    isPlaceholderData,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['dotypos-busyness-hourly', businessId, effectiveDateFrom, effectiveDateTo],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_busyness_hourly')
        .select('*')
        .eq('business_id', businessId)
        .gte('date', effectiveDateFrom)
        .lte('date', effectiveDateTo)
        .order('date', { ascending: true });

      if (error) throw error;
      return data as BusynessHourlyMetric[];
    },
    enabled: enabled && !!businessId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData, // Keep showing previous data while fetching new range
  });

  // Fetch last sync info
  const {
    data: lastSync,
    isLoading: isLoadingSync,
    refetch: refetchSync,
  } = useQuery({
    queryKey: ['dotypos-sync-log', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dotypos_sync_log')
        .select('*')
        .eq('business_id', businessId)
        .eq('sync_type', 'orders')
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as DotyposSyncLog | null;
    },
    enabled: enabled && !!businessId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

  // Check if Dotypos is configured
  const { data: dotyposConfig } = useQuery({
    queryKey: ['dotypos-config-status', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('businesses')
        .select('dotypos_enabled, dotypos_refresh_token, dotypos_cloud_id')
        .eq('id', businessId)
        .single();

      if (error) throw error;
      return {
        isConfigured:
          data.dotypos_enabled &&
          !!data.dotypos_refresh_token &&
          !!data.dotypos_cloud_id,
      };
    },
    enabled: enabled && !!businessId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for syncing orders
  const syncMutation = useMutation({
    mutationFn: async (options?: { dateFrom?: string; dateTo?: string; forceFullSync?: boolean }) => {
      // Call pass-generator-server instead of Edge Function
      // Dotypos API doesn't support Edge Functions
      const PASS_SERVER_URL = import.meta.env.DEV
        ? 'http://localhost:3001'
        : 'https://pass.taginn.app';

      const response = await fetch(`${PASS_SERVER_URL}/dotypos/sync-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          date_from: options?.dateFrom,
          date_to: options?.dateTo,
          force_full_sync: options?.forceFullSync,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Sync failed');

      return data as SyncOrdersResponse;
    },
    onSuccess: (data) => {
      toast.success('Sync completed', {
        description: `Synced ${data.orders_synced} orders from Dotypos`,
      });

      // Refetch data - invalidate all queries for this business
      queryClient.invalidateQueries({
        queryKey: ['dotypos-busyness-hourly', businessId],
        exact: false
      });
      queryClient.invalidateQueries({ queryKey: ['dotypos-sync-log', businessId] });
    },
    onError: (error: Error) => {
      toast.error('Sync failed', {
        description: error.message,
      });
    },
  });

  // Calculate aggregated statistics from hourly metrics
  const busynessStats = calculateBusynessStats(hourlyMetrics || [], daysDiff);

  return {
    // Data
    hourlyMetrics,
    busynessStats,
    lastSync,
    isDotyposConfigured: dotyposConfig?.isConfigured || false,

    // Loading states
    isLoading: isLoadingMetrics || isLoadingSync,
    isLoadingMetrics,
    isLoadingSync,
    isFetching: isFetchingMetrics,
    isPlaceholderData,
    isSyncing: syncMutation.isPending,

    // Errors
    error: metricsError,

    // Actions
    syncOrders: syncMutation.mutate,
    refetch: () => {
      refetchMetrics();
      refetchSync();
    },
  };
}

// Helper function to calculate aggregated statistics
function calculateBusynessStats(
  metrics: BusynessHourlyMetric[],
  dateRange: number
): BusynessStats | null {
  if (!metrics || metrics.length === 0) {
    return null;
  }

  // Group by hour of day (average across all days)
  const hourlyMap = new Map<number, { orders: number[]; revenue: number[] }>();
  for (let h = 0; h < 24; h++) {
    hourlyMap.set(h, { orders: [], revenue: [] });
  }

  // Group by day of week
  const dowMap = new Map<number, { orders: number[]; revenue: number[] }>();
  for (let d = 0; d < 7; d++) {
    dowMap.set(d, { orders: [], revenue: [] });
  }

  // Heatmap: hour x day of week
  const heatmapMap = new Map<string, { orders: number[]; revenue: number[] }>();
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      heatmapMap.set(`${d}_${h}`, { orders: [], revenue: [] });
    }
  }

  // Process all metrics
  let totalOrders = 0;
  let totalRevenue = 0;

  for (const metric of metrics) {
    // Hourly
    const hourData = hourlyMap.get(metric.hour_of_day)!;
    hourData.orders.push(metric.order_count);
    hourData.revenue.push(Number(metric.total_revenue));

    // Day of week
    const dowData = dowMap.get(metric.day_of_week)!;
    dowData.orders.push(metric.order_count);
    dowData.revenue.push(Number(metric.total_revenue));

    // Heatmap
    const heatmapKey = `${metric.day_of_week}_${metric.hour_of_day}`;
    const heatmapData = heatmapMap.get(heatmapKey)!;
    heatmapData.orders.push(metric.order_count);
    heatmapData.revenue.push(Number(metric.total_revenue));

    // Totals
    totalOrders += metric.order_count;
    totalRevenue += Number(metric.total_revenue);
  }

  // Calculate hourly pattern
  const hourlyPattern: HourlyData[] = [];
  for (let h = 0; h < 24; h++) {
    const data = hourlyMap.get(h)!;
    const avgOrders = data.orders.length > 0 ? average(data.orders) : 0;
    const avgRevenue = data.revenue.length > 0 ? average(data.revenue) : 0;
    hourlyPattern.push({
      hour: h,
      avgOrders,
      avgRevenue,
      totalOrders: sum(data.orders),
      totalRevenue: sum(data.revenue),
    });
  }

  // Calculate day of week pattern
  const dayOfWeekPattern: DayOfWeekData[] = [];
  for (let d = 0; d < 7; d++) {
    const data = dowMap.get(d)!;
    const avgOrders = data.orders.length > 0 ? average(data.orders) : 0;
    const avgRevenue = data.revenue.length > 0 ? average(data.revenue) : 0;
    dayOfWeekPattern.push({
      dayOfWeek: d,
      dayName: DAY_NAMES[d],
      avgOrders,
      avgRevenue,
      totalOrders: sum(data.orders),
      totalRevenue: sum(data.revenue),
    });
  }

  // Calculate heatmap data
  const heatmapData: HeatmapCell[] = [];
  let maxAvgOrders = 0;

  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const data = heatmapMap.get(`${d}_${h}`)!;
      const avgOrders = data.orders.length > 0 ? average(data.orders) : 0;
      const avgRevenue = data.revenue.length > 0 ? average(data.revenue) : 0;

      if (avgOrders > maxAvgOrders) {
        maxAvgOrders = avgOrders;
      }

      heatmapData.push({
        hour: h,
        dayOfWeek: d,
        dayName: DAY_NAMES[d],
        avgOrders,
        avgRevenue,
        intensity: 0, // Will be normalized later
      });
    }
  }

  // Normalize heatmap intensity
  for (const cell of heatmapData) {
    cell.intensity = maxAvgOrders > 0 ? cell.avgOrders / maxAvgOrders : 0;
  }

  // Find peak hours
  const peakHours: PeakTime[] = [...hourlyPattern]
    .sort((a, b) => b.avgOrders - a.avgOrders)
    .slice(0, 5)
    .map((data, index) => ({
      hour: data.hour,
      avgOrders: data.avgOrders,
      avgRevenue: data.avgRevenue,
      rank: index + 1,
    }));

  // Find peak days
  const peakDays: PeakDay[] = [...dayOfWeekPattern]
    .sort((a, b) => b.avgOrders - a.avgOrders)
    .map((data, index) => ({
      dayOfWeek: data.dayOfWeek,
      dayName: data.dayName,
      avgOrders: data.avgOrders,
      avgRevenue: data.avgRevenue,
      rank: index + 1,
    }));

  // Find busiest hour and day
  const busiestHour = peakHours.length > 0 ? peakHours[0].hour : 0;
  const busiestDay = peakDays.length > 0 ? peakDays[0].dayOfWeek : 0;

  // Calculate date range
  const dates = metrics.map((m) => m.date).sort();
  const dateFrom = dates[0] || '';
  const dateTo = dates[dates.length - 1] || '';

  // Calculate averages
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const avgOrdersPerDay = dateRange > 0 ? totalOrders / dateRange : 0;

  return {
    hourlyPattern,
    dayOfWeekPattern,
    heatmapData,
    peakHours,
    peakDays,
    totalOrders,
    totalRevenue,
    avgOrderValue,
    avgOrdersPerDay,
    busiestHour,
    busiestDay,
    dateFrom,
    dateTo,
  };
}

// Helper functions
function average(arr: number[]): number {
  if (arr.length === 0) return 0;
  return sum(arr) / arr.length;
}

function sum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

export default useDotyposBusyness;
